import React, { useState, useEffect, useMemo } from 'react';
import {
  MessageSquare, Clock, CheckCircle,
  Bell, RefreshCw,
} from 'lucide-react';
import {
  collection, query, where, orderBy, onSnapshot, limit, getDocs,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { STREAMS } from '../../data/doubtStreams';
import MathText from './MathText';
import styles from './AdminDoubtDashboard.module.css';

const PAGE_SIZE = 20;
const HOUR_24 = 24 * 60 * 60 * 1000;

function timeAgo(ts) {
  if (!ts) return '';
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function formatDateTime(ts) {
  if (!ts) return '';
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

export default function AdminDoubtDashboard({ onOpenDoubt }) {
  const [allDoubts, setAllDoubts]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [stats, setStats]           = useState({ total: 0, pending: 0, newToday: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [sortBy, setSortBy]         = useState('newest');
  const [replyPreviews, setReplyPreviews] = useState({});
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Reset visible count whenever any filter changes
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [statusFilter, sortBy, selectedUserId]);

  // ── Live stats (counts across ALL docs) ─────────────────────────
  useEffect(() => {
    const allQ = query(collection(db, 'doubts'));
    const opQ  = query(collection(db, 'doubts'), where('status', '==', 'open'));

    const unsub1 = onSnapshot(allQ, snap => {
      const newToday = snap.docs.filter(d => {
        const t = d.data().submittedAt?.toDate?.();
        return t && (Date.now() - t.getTime() < HOUR_24) && d.data().status !== 'answered';
      }).length;
      setStats(s => ({ ...s, total: snap.size, newToday }));
    });
    const unsub2 = onSnapshot(opQ, snap => {
      setStats(s => ({ ...s, pending: snap.size }));
    });

    return () => { unsub1(); unsub2(); };
  }, []);

  // ── Fetch all recent doubts — filter client-side ─────────────────
  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, 'doubts'), orderBy('submittedAt', 'desc'), limit(300));
    const unsub = onSnapshot(q, snap => {
      setAllDoubts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, err => { console.error('Admin doubts fetch failed:', err.message); setLoading(false); });
    return unsub;
  }, []);

  // ── Backfill last-reply preview for doubts that predate lastReplyText ──
  useEffect(() => {
    const missing = allDoubts.filter(d => d.replyCount > 0 && !d.lastReplyText && !replyPreviews[d.id]);
    if (missing.length === 0) return;
    Promise.all(
      missing.map(async d => {
        try {
          const snap = await getDocs(
            query(collection(db, 'doubts', d.id, 'replies'), orderBy('createdAt', 'desc'), limit(1))
          );
          if (!snap.empty) {
            const r = snap.docs[0].data();
            return [d.id, { text: r.text || '(image attached)', by: r.roleLabel || r.userName || 'Student' }];
          }
        } catch {}
        return null;
      })
    ).then(results => {
      const map = {};
      results.forEach(r => { if (r) map[r[0]] = r[1]; });
      if (Object.keys(map).length > 0) setReplyPreviews(prev => ({ ...prev, ...map }));
    });
  }, [allDoubts]);

  // ── Client-side filtering & sorting ─────────────────────────────
  const isFollowupState = d => d.status !== 'answered' && (d.lastRepliedByRole === 'student' || (d.replyCount > 0 && d.status === 'open'));

  // Group doubts by student, sorted by their latest activity
  const studentGroups = useMemo(() => {
    const map = {};
    allDoubts.forEach(d => {
      if (!map[d.userId]) map[d.userId] = {
        userId: d.userId, userName: d.userName || 'Student',
        userPhotoURL: d.userPhotoURL, doubts: [],
        pending: 0, followup: 0, answered: 0, latestActivity: null,
      };
      const g = map[d.userId];
      g.doubts.push(d);
      if (d.status === 'answered') g.answered++;
      else if (isFollowupState(d)) g.followup++;
      else g.pending++;
      const t = d.lastActivityAt || d.submittedAt;
      if (!g.latestActivity || (t?.toMillis?.() ?? 0) > (g.latestActivity?.toMillis?.() ?? 0)) g.latestActivity = t;
    });
    return Object.values(map).sort((a, b) => (b.latestActivity?.toMillis?.() ?? 0) - (a.latestActivity?.toMillis?.() ?? 0));
  }, [allDoubts]);

  function selectStudent(uid) {
    setSelectedUserId(uid);
    setStatusFilter('all');
  }

  // Base doubts scoped to selected student (or all)
  const baseDoubts = useMemo(() =>
    selectedUserId ? allDoubts.filter(d => d.userId === selectedUserId) : allDoubts,
    [allDoubts, selectedUserId]
  );

  const followupCount = useMemo(() => baseDoubts.filter(isFollowupState).length, [baseDoubts]);
  const pendingOnlyCount = useMemo(() => baseDoubts.filter(d => d.status !== 'answered' && !isFollowupState(d)).length, [baseDoubts]);
  const answeredCount = useMemo(() => baseDoubts.filter(d => d.status === 'answered').length, [baseDoubts]);

  const filteredDoubts = useMemo(() => {
    let result = baseDoubts;

    if (statusFilter !== 'all') {
      if (statusFilter === 'answered') {
        result = result.filter(d => d.status === 'answered');
      } else if (statusFilter === 'followup') {
        result = result.filter(d => isFollowupState(d));
      } else {
        result = result.filter(d => d.status !== 'answered' && !isFollowupState(d));
      }
    }
    if (sortBy === 'unanswered') {
      result = [...result].sort((a, b) => {
        const aPending = a.status !== 'answered' ? 0 : 1;
        const bPending = b.status !== 'answered' ? 0 : 1;
        if (aPending !== bPending) return aPending - bPending;
        return (b.submittedAt?.toMillis?.() ?? 0) - (a.submittedAt?.toMillis?.() ?? 0);
      });
    }

    return result;
  }, [baseDoubts, statusFilter, sortBy]);

  const visibleDoubts = filteredDoubts.slice(0, visibleCount);
  const hasMore = filteredDoubts.length > visibleCount;

  function loadMore() { setVisibleCount(c => c + PAGE_SIZE); }

  return (
    <div className={styles.dash}>
      {/* ── Stats bar (global) ─────────────────────────────────── */}
      <div className={styles.statsBar}>
        <div className={styles.statCard}>
          <MessageSquare size={16} className={styles.statIcon} />
          <div><span className={styles.statNum}>{stats.total}</span><span className={styles.statLabel}>Total</span></div>
        </div>
        <div className={`${styles.statCard} ${styles.statPending}`}>
          <Clock size={16} className={styles.statIcon} />
          <div><span className={styles.statNum}>{stats.pending - followupCount}</span><span className={styles.statLabel}>Pending</span></div>
        </div>
        {followupCount > 0 && (
          <div className={`${styles.statCard} ${styles.statFollowup}`}>
            <RefreshCw size={16} className={styles.statIcon} />
            <div><span className={styles.statNum}>{followupCount}</span><span className={styles.statLabel}>Follow-up</span></div>
          </div>
        )}
        <div className={`${styles.statCard} ${styles.statAnswered}`}>
          <CheckCircle size={16} className={styles.statIcon} />
          <div><span className={styles.statNum}>{stats.total - stats.pending}</span><span className={styles.statLabel}>Answered</span></div>
        </div>
        {stats.newToday > 0 && (
          <div className={`${styles.statCard} ${styles.statNew}`}>
            <Bell size={16} className={styles.statIcon} />
            <div><span className={styles.statNum}>{stats.newToday}</span><span className={styles.statLabel}>New (24h)</span></div>
          </div>
        )}
      </div>

      {/* ── Body: student panel + doubt feed ──────────────────── */}
      <div className={styles.dashBody}>

        {/* ── Left: student sidebar ─────────────────────────── */}
        <div className={styles.studentPanel}>
          <div className={styles.studentPanelHead}>Students</div>

          <button
            className={`${styles.studentItem} ${!selectedUserId ? styles.studentItemActive : ''}`}
            onClick={() => selectStudent(null)}
          >
            <div className={`${styles.sAvatarFallback} ${styles.sAvatarAll}`}>All</div>
            <div className={styles.sInfo}>
              <span className={styles.sName}>All Students</span>
              <span className={styles.sMeta}>{studentGroups.length} students · {stats.total} doubts</span>
            </div>
            {(stats.pending > 0) && <span className={styles.sBadgePending}>{stats.pending}</span>}
          </button>

          {studentGroups.map(g => (
            <button
              key={g.userId}
              className={`${styles.studentItem} ${selectedUserId === g.userId ? styles.studentItemActive : ''}`}
              onClick={() => selectStudent(g.userId)}
            >
              {g.userPhotoURL
                ? <img src={g.userPhotoURL} alt="" className={styles.sAvatar} referrerPolicy="no-referrer" />
                : <div className={styles.sAvatarFallback}>{(g.userName[0] ?? '?').toUpperCase()}</div>
              }
              <div className={styles.sInfo}>
                <span className={styles.sName}>{g.userName}</span>
                <span className={styles.sMeta}>{g.doubts.length} doubt{g.doubts.length !== 1 ? 's' : ''} · {timeAgo(g.latestActivity)}</span>
              </div>
              <div className={styles.sBadges}>
                {g.followup > 0 && <span className={styles.sBadgeFollowup}>{g.followup}</span>}
                {g.pending > 0  && <span className={styles.sBadgePending}>{g.pending}</span>}
              </div>
            </button>
          ))}
        </div>

        {/* ── Right: filters + doubt cards ──────────────────── */}
        <div className={styles.doubtFeed}>
          {selectedUserId && (
            <div className={styles.feedStudentHeader}>
              {(() => {
                const g = studentGroups.find(s => s.userId === selectedUserId);
                return g ? (
                  <>
                    {g.userPhotoURL
                      ? <img src={g.userPhotoURL} alt="" className={styles.feedAvatar} referrerPolicy="no-referrer" />
                      : <div className={styles.feedAvatarFallback}>{(g.userName?.[0] ?? '?').toUpperCase()}</div>
                    }
                    <span className={styles.feedStudentName}>{g.userName}</span>
                    <span className={styles.feedStudentMeta}>{g.doubts.length} doubts</span>
                    <button className={styles.feedClearBtn} onClick={() => selectStudent(null)}>✕ All Students</button>
                  </>
                ) : null;
              })()}
            </div>
          )}

          {/* Status tabs + sort */}
          <div className={styles.toolbar}>
            <div className={styles.statusTabs}>
              {[['all','All',null],['open','Pending',pendingOnlyCount],['followup','Follow-up',followupCount],['answered','Answered',answeredCount]].map(([v, l, count]) => (
                <button
                  key={v}
                  className={`${styles.statusTab} ${statusFilter === v ? styles.statusTabActive : ''}`}
                  onClick={() => setStatusFilter(v)}
                >{l}{count > 0 && <span className={styles.tabBadge}>{count}</span>}</button>
              ))}
            </div>
            <select className={styles.sortSelect} value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="newest">⏰ Newest First</option>
              <option value="unanswered">📋 Pending First</option>
            </select>
          </div>

          {/* Doubt cards */}
          <div className={styles.board}>
            {loading && <div className={styles.cardGrid}>{[1,2,3,4,5,6].map(i => <div key={i} className={styles.skeleton} />)}</div>}
            {!loading && filteredDoubts.length === 0 && (
              <div className={styles.emptyState}>
                <span className={styles.emptyEmoji}>🎉</span>
                <p className={styles.emptyTitle}>All clear!</p>
                <p className={styles.emptyHint}>No doubts match the selected filters.</p>
              </div>
            )}
            {!loading && visibleDoubts.length > 0 && (
              <div className={styles.cardGrid}>
                {visibleDoubts.map(doubt => {
                  const isNew = doubt.submittedAt?.toDate && (Date.now() - doubt.submittedAt.toDate().getTime() < HOUR_24);
                  const isAnswered = doubt.status === 'answered';
                  const isFollowup = !isAnswered && (doubt.lastRepliedByRole === 'student' || (doubt.replyCount > 0 && doubt.status === 'open'));
                  return (
                    <button
                      key={doubt.id}
                      className={`${styles.doubtCard} ${isAnswered ? styles.cardAnswered : isFollowup ? styles.cardFollowup : styles.cardOpen}`}
                      onClick={() => onOpenDoubt(doubt)}
                    >
                      <div className={styles.cardHead}>
                        <div className={styles.cardAvatarWrap}>
                          {doubt.userPhotoURL
                            ? <img src={doubt.userPhotoURL} alt="" className={styles.cardAvatar} referrerPolicy="no-referrer" />
                            : <div className={styles.cardAvatarFallback}>{(doubt.userName?.[0] ?? '?').toUpperCase()}</div>
                          }
                        </div>
                        <div className={styles.cardMeta}>
                          {!selectedUserId && <span className={styles.cardName}>{doubt.userName || 'Student'}</span>}
                          <span className={styles.cardTime}>{formatDateTime(doubt.submittedAt)}</span>
                          <span className={styles.cardTimeAgo}>{timeAgo(doubt.submittedAt)}</span>
                        </div>
                        <div className={styles.cardBadges}>
                          {isNew && !isAnswered && <span className={styles.newBadge}>NEW</span>}
                          <span className={`${styles.statusDot} ${isAnswered ? styles.dotAnswered : isFollowup ? styles.dotFollowup : styles.dotOpen}`} />
                        </div>
                      </div>
                      <div className={styles.cardTags}>
                        <span className={styles.streamTag}>{STREAMS[doubt.stream]?.label ?? doubt.stream}</span>
                        {doubt.subject && <span className={styles.subjectTag}>{doubt.subject}</span>}
                        {doubt.chapter && <span className={styles.chapterTag}>{doubt.chapter}</span>}
                      </div>
                      <p className={styles.cardQuestion}>
                        {(() => {
                          const preview = doubt.lastReplyText ? { text: doubt.lastReplyText, by: doubt.lastReplyBy } : replyPreviews[doubt.id];
                          return preview
                            ? <><span className={styles.previewBy}>{preview.by}:</span> <MathText text={preview.text} /></>
                            : <MathText text={doubt.questionText || '(image attached)'} />;
                        })()}
                      </p>
                      <div className={styles.cardFoot}>
                        <span className={`${styles.statusLabel} ${isAnswered ? styles.labelAnswered : isFollowup ? styles.labelFollowup : styles.labelOpen}`}>
                          {isAnswered ? '✓ Answered' : isFollowup ? '↩ Follow-up' : '● Pending'}
                        </span>
                        {doubt.replyCount > 0 && <span className={styles.replyCount}><MessageSquare size={11} /> {doubt.replyCount}</span>}
                        <span className={styles.answerCta}>{isAnswered ? 'View' : isFollowup ? 'Reply' : 'Answer'} →</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {hasMore && (
              <div className={styles.loadMoreWrap}>
                <button className={styles.loadMoreBtn} onClick={loadMore}>Load More ({filteredDoubts.length - visibleCount} remaining)</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
